import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DEV_PACKAGE_TIER_STORAGE_KEY,
  DEV_VIEW_MODE_STORAGE_KEY,
} from "./constants";
import { canAdminUseDevViewOverride } from "./isDevViewModeEnabled";
import {
  isValidViewMode,
  readStoredDevViewMode,
  resolveAuthViewMode,
  writeStoredDevViewMode,
} from "./resolveAuthViewMode";
import {
  isValidPackageTier,
  readStoredDevPackageTier,
  writeStoredDevPackageTier,
} from "./resolveDevPackageTier";

/** @typedef {import("./constants").ViewMode} ViewMode */
/** @typedef {import("./constants").PackageTier} PackageTier */

/**
 * @typedef {Object} DevViewModeContextValue
 * @property {ViewMode} mode
 * @property {(next: ViewMode) => void} setMode
 * @property {PackageTier|null} packageTier
 * @property {(next: PackageTier|null) => void} setPackageTier
 * @property {boolean} canSwitch
 * @property {boolean} isDevOverrideActive
 * @property {boolean} isPackageOverrideActive
 * @property {boolean} presentAsAdmin
 */

const DevViewModeContext = createContext(
  /** @type {DevViewModeContextValue | null} */ (null),
);

function useAuthViewModeTick() {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const bump = () => setTick((value) => value + 1);

    window.addEventListener("storage", bump);
    window.addEventListener("hr:user-info-updated", bump);

    return () => {
      window.removeEventListener("storage", bump);
      window.removeEventListener("hr:user-info-updated", bump);
    };
  }, []);

  return null;
}

export function DevViewModeProvider({ children }) {
  useAuthViewModeTick();
  const devEnabled = canAdminUseDevViewOverride();
  const [devOverride, setDevOverride] = useState(() =>
    devEnabled ? readStoredDevViewMode() : null,
  );
  const [packageOverride, setPackageOverride] = useState(() =>
    devEnabled ? readStoredDevPackageTier() : null,
  );

  const authMode = resolveAuthViewMode();

  const mode = devEnabled && devOverride != null ? devOverride : authMode;
  const packageTier = devEnabled ? packageOverride : null;
  const isPackageOverrideActive = Boolean(devEnabled && packageTier);

  const setMode = useCallback(
    (next) => {
      if (!devEnabled || !isValidViewMode(next)) return;
      setDevOverride(next);
      writeStoredDevViewMode(next);
      // Admin / customer chrome is orthogonal to package preview — clear package when leaving agent.
      if (next !== "agent" && packageOverride != null) {
        setPackageOverride(null);
        writeStoredDevPackageTier(null);
      }
    },
    [devEnabled, packageOverride],
  );

  const setPackageTier = useCallback(
    (next) => {
      if (!devEnabled) return;
      if (next != null && !isValidPackageTier(next)) return;
      setPackageOverride(next);
      writeStoredDevPackageTier(next);
      if (next != null) {
        // Package preview is an agent commercial view.
        setDevOverride("agent");
        writeStoredDevViewMode("agent");
      }
    },
    [devEnabled],
  );

  useEffect(() => {
    if (!devEnabled) return undefined;

    const onStorage = (event) => {
      if (event.key === DEV_VIEW_MODE_STORAGE_KEY) {
        setDevOverride(readStoredDevViewMode());
      }
      if (event.key === DEV_PACKAGE_TIER_STORAGE_KEY) {
        setPackageOverride(readStoredDevPackageTier());
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [devEnabled]);

  const presentAsAdmin =
    !isPackageOverrideActive && mode === "admin" && authMode === "admin";

  const value = useMemo(
    () => ({
      mode,
      setMode,
      packageTier,
      setPackageTier,
      canSwitch: devEnabled,
      isDevOverrideActive: devEnabled && devOverride != null,
      isPackageOverrideActive,
      presentAsAdmin,
    }),
    [
      mode,
      setMode,
      packageTier,
      setPackageTier,
      devEnabled,
      devOverride,
      isPackageOverrideActive,
      presentAsAdmin,
    ],
  );

  return (
    <DevViewModeContext.Provider value={value}>{children}</DevViewModeContext.Provider>
  );
}

/** @returns {DevViewModeContextValue} */
export function useDevViewMode() {
  const context = useContext(DevViewModeContext);
  if (!context) {
    throw new Error("useDevViewMode must be used within DevViewModeProvider");
  }
  return context;
}

/**
 * Safe hook when provider may be absent (tests / isolated trees).
 * @returns {DevViewModeContextValue | null}
 */
export function useDevViewModeOptional() {
  return useContext(DevViewModeContext);
}
